/**
 * Por recibir — lo vendido a pedido que falta que llegue, y lo que llegó y
 * el cliente todavía no se llevó.
 *
 * Dos listas y no una con filtro: son dos tareas distintas de dos personas
 * distintas (el que le reclama al proveedor y el que atiende el mostrador).
 * Lo más viejo arriba: es lo que más hace esperar al cliente.
 */
import { useState } from "react";
import { PackageCheck, PackageOpen } from "lucide-react";
import { type Moneda } from "@tora/shared";

import { InputImporte } from "@/components/money/input-importe";
import { Importe } from "@/components/money/importe";
import { nombreCompleto } from "@/components/catalogo/nombre";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useEncargos, useEntregarEncargo, useRecibirEncargo } from "@/lib/queries";
import { aNumero, type Encargo } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

function nombreDelEquipo(e: Encargo): string {
  return nombreCompleto({
    nombre: e.producto,
    color: e.color,
    memoria_gb: e.memoria_gb,
    bateria_pct: e.bateria_pct,
  });
}

/**
 * `recibido`: el renglon va en «Llegó, falta entregar». Ahi decia «lo tiene
 * Celplaza» debajo del titulo «Ya está en el local».
 */
function dondeEsta(e: Encargo, recibido: boolean): string {
  if (!recibido) return e.proveedor ? ` · lo tiene ${e.proveedor}` : "";
  // `proveedor` es el anotado al vender: un encargo de Celplaza que llega en
  // una compra de Morly se tilda ahi igual, y decia «llegó de Celplaza». El
  // origen sale de la compra; sin compra (recibido a mano) no se afirma.
  if (e.proveedor_compra) return ` · llegó de ${e.proveedor_compra}`;
  return e.proveedor ? ` · encargado a ${e.proveedor}` : "";
}

function Renglon({
  e,
  accion,
  recibido = false,
}: {
  e: Encargo;
  accion: JSX.Element;
  recibido?: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-medium">
          {nombreDelEquipo(e)}
          {/* Lo que pidio el cliente (color, variante), anotado al vender. */}
          {e.nota ? <span className="font-normal text-muted-foreground"> · {e.nota}</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">
          {/* Con el año entero, como el resto de las fechas: «30/09/26» era la
              unica pantalla con dos digitos. */}
          {e.cliente ?? "Consumidor final"} · {e.venta} · vendido el {formatearFecha(e.fecha_venta)}
          {dondeEsta(e, recibido)}
        </p>
      </div>
      {accion}
    </li>
  );
}

/**
 * Si la lista no se pudo leer, decirlo. Mostrar «No hay nada» en ese caso
 * esconde encargos que existen: el cliente espera y nadie lo reclama.
 */
function NoSePudoLeer(): JSX.Element {
  return (
    <p className="text-sm text-destructive">
      No se pudo leer esta lista. Recargá la página; si sigue igual, avisá.
    </p>
  );
}

export function PorRecibir({ cotizacion }: { cotizacion?: number }): JSX.Element {
  const { data: porRecibir, isError: fallaRecibir } = useEncargos({ estado: "POR_RECIBIR" });
  const { data: porEntregar, isError: fallaEntregar } = useEncargos({ estado: "POR_ENTREGAR" });
  const [recibiendo, setRecibiendo] = useState<Encargo | null>(null);
  const [entregando, setEntregando] = useState<Encargo | null>(null);

  const faltan = porRecibir?.filas ?? [];
  const llegaron = porEntregar?.filas ?? [];

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <TituloSeccion descripcion="Vendido a pedido. Si llega con factura, se marca solo al cargarla en Compras.">
          Falta que llegue
        </TituloSeccion>
        {fallaRecibir ? (
          <NoSePudoLeer />
        ) : faltan.length === 0 ? (
          <EstadoVacio icono={PackageOpen} titulo="No hay nada por recibir" tamano="sm" />
        ) : (
          <Card padding="normal">
            <ul className="divide-y">
              {faltan.map((e) => (
                <Renglon
                  key={e.id}
                  e={e}
                  accion={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRecibiendo(e)}
                    >
                      Recibido
                    </Button>
                  }
                />
              ))}
            </ul>
          </Card>
        )}
      </section>

      <section className="space-y-2">
        <TituloSeccion descripcion="Ya está en el local. Marcalo cuando el cliente se lo lleve.">
          Llegó, falta entregar
        </TituloSeccion>
        {fallaEntregar ? (
          <NoSePudoLeer />
        ) : llegaron.length === 0 ? (
          <EstadoVacio icono={PackageCheck} titulo="No hay nada por entregar" tamano="sm" />
        ) : (
          <Card padding="normal">
            <ul className="divide-y">
              {llegaron.map((e) => (
                <Renglon
                  key={e.id}
                  e={e}
                  recibido
                  accion={
                    <Button type="button" size="sm" onClick={() => setEntregando(e)}>
                      Entregado
                    </Button>
                  }
                />
              ))}
            </ul>
          </Card>
        )}
      </section>

      <DialogEntregar encargo={entregando} onCerrar={() => setEntregando(null)} />

      {/* La key lo rearma por encargo: arranca con SU costo estimado. */}
      <DialogRecibir
        key={recibiendo?.id ?? "ninguno"}
        encargo={recibiendo}
        cotizacion={cotizacion}
        onCerrar={() => setRecibiendo(null)}
      />
    </div>
  );
}

/**
 * Confirmar la entrega. Antes el boton la marcaba de un toque, sin preguntar y
 * sin forma de deshacerla: un toque de mas sacaba el encargo de la lista y
 * nadie volvia a saber que el cliente todavia lo esperaba.
 */
function DialogEntregar({
  encargo,
  onCerrar,
}: {
  encargo: Encargo | null;
  onCerrar: () => void;
}): JSX.Element {
  const entregar = useEntregarEncargo();

  function confirmar(): void {
    if (!encargo) return;
    entregar.mutate(encargo.id, {
      onSuccess: () => {
        mostrarExito(`${nombreDelEquipo(encargo)} entregado`, encargo.venta);
        onCerrar();
      },
      onError: mostrarError,
    });
  }

  return (
    <Dialog
      open={encargo !== null}
      onOpenChange={(a) => {
        if (!a && !entregar.isPending) onCerrar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>¿{encargo?.cliente ?? "El cliente"} ya se lo llevó?</DialogTitle>
          <DialogDescription>
            {encargo ? `${nombreDelEquipo(encargo)} · ${encargo.venta}` : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            Sale de la lista de lo que falta entregar, y no se puede volver atrás.
          </p>
          {/* Una seña y el resto a la cuenta: el equipo se entregaba sin que
              nadie viera que la venta todavia se debia. La deuda sigue en su
              cuenta corriente; esto solo lo avisa antes de que se lo lleve. */}
          {encargo && debeDeLaVenta(encargo) > 0.009 && (
            <Aviso tono="warning" compacto className="mt-3">
              Todavía debe{" "}
              <Importe
                monto={debeDeLaVenta(encargo)}
                moneda={encargo.venta_moneda ?? "ARS"}
                tamano="sm"
              />{" "}
              de {encargo.venta}. Cobrale desde su ficha en Clientes; si no, queda en su cuenta
              corriente.
            </Aviso>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={entregar.isPending}>
            Cancelar
          </Button>
          <Button type="button" cargando={entregar.isPending} onClick={confirmar}>
            Sí, lo entregué
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Lo que falta cobrar de la venta del encargo (0121_9a). Sin el dato, cero. */
function debeDeLaVenta(e: Encargo): number {
  return e.venta_pendiente == null ? 0 : aNumero(e.venta_pendiente);
}

/** Llegó sin factura: con qué costo. Viene con el estimado de la venta. */
function DialogRecibir({
  encargo,
  cotizacion,
  onCerrar,
}: {
  encargo: Encargo | null;
  cotizacion?: number;
  onCerrar: () => void;
}): JSX.Element {
  const recibir = useRecibirEncargo();
  const [costo, setCosto] = useState<number | null>(
    encargo?.costo_estimado == null ? null : aNumero(encargo.costo_estimado),
  );
  const [moneda, setMoneda] = useState<Moneda>(encargo?.moneda_estimada ?? "USD");

  function confirmar(): void {
    if (!encargo || costo === null) return;
    recibir.mutate(
      { id: encargo.id, costo, moneda },
      {
        onSuccess: () => {
          mostrarExito(`${nombreDelEquipo(encargo)} recibido`, "Queda para entregar al cliente.");
          onCerrar();
        },
        onError: mostrarError,
      },
    );
  }

  return (
    <Dialog
      open={encargo !== null}
      onOpenChange={(a) => {
        if (!a) onCerrar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Llegó el encargo</DialogTitle>
          <DialogDescription>
            {encargo ? `${nombreDelEquipo(encargo)} · ${encargo.venta}` : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <InputImporte
            label="Costo real: cuánto lo cobró el proveedor"
            valor={costo}
            onValorChange={setCosto}
            moneda={moneda}
            onMonedaChange={setMoneda}
            cotizacion={cotizacion}
            requerido
            ayuda="Si llegó con factura, cargala en Compras: ahí se marca solo."
          />
          {encargo?.costo_estimado !== null && encargo?.costo_estimado !== undefined ? (
            <p className="text-xs text-muted-foreground">
              Estimado al vender:{" "}
              <Importe
                monto={aNumero(encargo.costo_estimado)}
                moneda={encargo.moneda_estimada ?? "USD"}
                tamano="sm"
              />
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={costo === null}
            cargando={recibir.isPending}
            onClick={confirmar}
          >
            Marcar recibido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
